import { Color } from "@tiptap/extension-color";
import { History } from "@tiptap/extension-history";
import { CustomImage } from "../components/dashboard/editor/extensions/CustomImage";
import Link from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import StarterKit from "@tiptap/starter-kit";
import React from "react";
import showdown from "showdown";
import TurndownService from "turndown";
import { Highlight } from "../components/dashboard/editor/extensions/highlight";
import { FactCheckDecorations } from "../components/dashboard/editor/extensions/FactCheckDecorations";
import { UnlockCard } from "../components/dashboard/editor/extensions/UnlockCard";

// Debounce utility
export const useDebounce = (func: Function, wait: number) => {
    const timeout = React.useRef<NodeJS.Timeout>();

    return React.useCallback(
        (...args: any[]) => {
            const later = () => {
                clearTimeout(timeout.current);
                func(...args);
            };

            clearTimeout(timeout.current);
            timeout.current = setTimeout(later, wait);
        },
        [func, wait]
    );
};

/**
 * Dual-channel debounce for Humanized vs Original content
 * Each channel has independent timer to prevent cancellation during toggle
 * Includes real flush() that awaits async save completion
 */
export const useDualDebounce = (func: Function, wait: number) => {
    const timeouts = React.useRef<{
        humanized: NodeJS.Timeout | null;
        original: NodeJS.Timeout | null;
    }>({ humanized: null, original: null });

    const pendingArgs = React.useRef<{
        humanized: [string, any] | null;
        original: [string, any] | null;
    }>({ humanized: null, original: null });

    // Track in-flight save promises to await them in flush
    const inFlightPromises = React.useRef<{
        humanized: Promise<any> | null;
        original: Promise<any> | null;
    }>({ humanized: null, original: null });

    // Store latest func to avoid stale closures
    const funcRef = React.useRef(func);
    React.useEffect(() => {
        funcRef.current = func;
    }, [func]);

    const debounce = React.useCallback(
        (content: string, options: { isHumanized: boolean; [key: string]: any }) => {
            const channel = options.isHumanized ? 'humanized' : 'original';

            // Store args for potential flush
            pendingArgs.current[channel] = [content, options];

            const later = () => {
                timeouts.current[channel] = null;
                const args = pendingArgs.current[channel];
                pendingArgs.current[channel] = null;

                if (args) {
                    // Execute and track the promise
                    const promise = Promise.resolve(funcRef.current(...args));
                    inFlightPromises.current[channel] = promise;

                    // Clear promise when done. The trailing catch only stops an
                    // unhandled rejection: a failed save already showed its error,
                    // and flush() still sees the rejection via `promise`.
                    promise.finally(() => {
                        if (inFlightPromises.current[channel] === promise) {
                            inFlightPromises.current[channel] = null;
                        }
                    }).catch(() => {});
                }
            };

            if (timeouts.current[channel]) {
                clearTimeout(timeouts.current[channel]!);
            }
            timeouts.current[channel] = setTimeout(later, wait);
        },
        [wait]
    );

    const flush = React.useCallback(async (channel?: 'humanized' | 'original') => {
        const channels = channel ? [channel] : (['humanized', 'original'] as const);

        const promiseSet = new Set<Promise<any>>();

        channels.forEach(ch => {
            // Cancel pending timer and execute immediately
            if (timeouts.current[ch]) {
                clearTimeout(timeouts.current[ch]!);
                timeouts.current[ch] = null;
            }

            // Capture older in-flight save if exists (must wait for this too)
            if (inFlightPromises.current[ch]) {
                promiseSet.add(inFlightPromises.current[ch]!);
            }

            const args = pendingArgs.current[ch];
            if (args) {
                pendingArgs.current[ch] = null;
                const promise = Promise.resolve(funcRef.current(...args));
                inFlightPromises.current[ch] = promise;
                promiseSet.add(promise);

                promise.finally(() => {
                    if (inFlightPromises.current[ch] === promise) {
                        inFlightPromises.current[ch] = null;
                    }
                }).catch(() => {}); // rejection is surfaced by the Promise.all below
            }
        });

        // Wait for all saves to complete (Set automatically dedupes)
        if (promiseSet.size > 0) {
            await Promise.all(Array.from(promiseSet));
        }
    }, []);

    const cancel = React.useCallback(() => {
        (['humanized', 'original'] as const).forEach(ch => {
            if (timeouts.current[ch]) {
                clearTimeout(timeouts.current[ch]!);
                timeouts.current[ch] = null;
            }
            pendingArgs.current[ch] = null;
            // Note: We don't cancel in-flight promises, just clear pending args
        });
    }, []);

    return { debounce, flush, cancel };
};

// Common editor extensions
export const getEditorExtensions = () => [
    StarterKit.configure({
        history: false, // Disable history in StarterKit
    }),
    History.configure({
        depth: 100,
        newGroupDelay: 500,
    }),
    Highlight,
    FactCheckDecorations,
    UnlockCard,
    Underline,
    TextStyle,
    Link,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Color.configure({ types: ["textStyle"] }),
    Table.configure({
        resizable: true,
        HTMLAttributes: {
            class: "my-custom-class",
        },
    }),
    TableRow,
    TableHeader,
    TableCell,
    CustomImage.configure({
        inline: true,
        allowBase64: true,
    }),
];

// Setup Turndown service for HTML to Markdown conversion
export const setupTurndownService = () => {
    const turndownService = new TurndownService({
        headingStyle: "atx",
        codeBlockStyle: "fenced",
        bulletListMarker: "-",
        strongDelimiter: "**",
        emDelimiter: "_",
    });

    // Add table support with link preservation
    turndownService.addRule("table", {
        filter: "table",
        replacement: function (content, node) {
            const rows = node.querySelectorAll("tr");
            let markdown = "\n";

            rows.forEach((row, rowIndex) => {
                const cells = row.querySelectorAll("th, td");
                let rowContent = "|";

                cells.forEach((cell) => {
                    // Create a temporary container to handle nested elements
                    const tempDiv = document.createElement("div");
                    tempDiv.innerHTML = cell.innerHTML;

                    // Process links in the cell
                    const links = tempDiv.querySelectorAll("a");
                    links.forEach((link) => {
                        const text = link.textContent || "";
                        const href = link.getAttribute("href") || "";
                        const mdLink = `[${text}](${href})`;
                        // Replace the link element with its markdown equivalent
                        link.outerHTML = mdLink;
                    });

                    rowContent += ` ${tempDiv.textContent.trim()} |`;
                });

                markdown += rowContent + "\n";

                // Add separator after header row
                if (rowIndex === 0) {
                    markdown += "|" + " --- |".repeat(cells.length);
                    markdown += "\n";
                }
            });

            return markdown + "\n";
        },
    });

    // Keep specific HTML elements
    turndownService.keep(["sup", "sub", "mark"]);

    // Add rule for links (to ensure they're properly handled everywhere)
    turndownService.addRule("links", {
        filter: "a",
        replacement: function (content, node) {
            const href = (node as HTMLAnchorElement).getAttribute("href") || "";
            return `[${content}](${href})`;
        },
    });

    // Remove unwanted elements
    turndownService.remove(["script", "style", "meta"]);

    return turndownService;
};

// Get a configured showdown converter
export const getMarkdownConverter = () => {
    return new showdown.Converter({
        tables: true,
        tasklists: true,
        strikethrough: true,
        emoji: true,
    });
};

// Add meta information to article
export const addMetaToArticle = (html: string, title: string, description: string) => {
    return `
  <p><strong>meta title:</strong> ${title}</p>
  <p><strong>meta description:</strong> ${description}</p>
  ${html}
  `;
};

// Helper to count selected words
export const countSelectedWords = (editor: any) => {
    if (!editor) return 0;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    return selectedText.split(" ").length;
};

// Define the type for selectedText items
export interface SelectedTextItem {
    text: string;
    element: string;
    html: string;
    from: number;
    to: number;
}

// Helper to extract selected blocks for AI operations
export const extractSelectedBlocks = (editor: any, from: number, to: number, converter: any): SelectedTextItem[] => {
    const selectedBlocks: SelectedTextItem[] = [];

    editor.state.doc.nodesBetween(from, to, (node: any, pos: number) => {
        if (node.isBlock) {
            let htmlElement = "span"; // Default
            if (node.type.name === "heading") {
                const level = node.attrs.level || 1;
                htmlElement = `h${level}`;
            } else if (node.type.name === "paragraph") {
                htmlElement = "p";
            } else {
                const elementTagMap: { [key: string]: string } = {
                    blockquote: "blockquote",
                    code_block: "pre",
                    // Add other block-level elements here
                };
                htmlElement = elementTagMap[node.type.name] || "span";
            }

            const nodeText = node.textContent || "";
            const innerHtml = converter.makeHtml(nodeText);
            const cleanInnerHtml = innerHtml.replace(/<[^>]*>?/g, "");

            const html = `<${htmlElement}>${cleanInnerHtml}</${htmlElement}>`;

            selectedBlocks.push({
                text: nodeText,
                element: htmlElement,
                html: html,
                from: pos,
                to: pos + node.nodeSize - 1, // Adjusted to exclude node boundaries
            });
        }
    });

    return selectedBlocks;
};

// Get word count from text
export const getWordCount = (text: string): number => {
    if (!text || text.trim().length === 0) {
        return 0;
    }
    const words = text.trim().split(/\s+/);
    return words.length;
};
